<template>
  <div class="excel-editor">
    <div class="excel-toolbar">
      <div class="toolbar-group">
        <el-button size="small" :disabled="readonly" @click="undo">
          <el-icon><RefreshLeft /></el-icon>
          <span>撤销</span>
        </el-button>
        <el-button size="small" :disabled="readonly" @click="redo">
          <el-icon><RefreshRight /></el-icon>
          <span>重做</span>
        </el-button>
        <el-input
          v-model="sheetName"
          size="small"
          class="sheet-name-input"
          :disabled="readonly"
          maxlength="31"
          placeholder="工作表名称"
        />
      </div>
      <div class="toolbar-group">
        <el-button size="small" :disabled="readonly" @click="addRow">
          <el-icon><Plus /></el-icon>
          <span>添加行</span>
        </el-button>
        <el-button size="small" :disabled="readonly" @click="addColumn">
          <el-icon><Plus /></el-icon>
          <span>添加列</span>
        </el-button>
        <el-button size="small" :disabled="readonly || rows.length === 0" @click="duplicateRow">
          <el-icon><CopyDocument /></el-icon>
          <span>复制行</span>
        </el-button>
        <el-button size="small" :disabled="readonly || columnCount === 0" @click="duplicateColumn">
          <el-icon><CopyDocument /></el-icon>
          <span>复制列</span>
        </el-button>
        <el-button size="small" type="danger" plain :disabled="readonly || rows.length === 0" @click="removeRow">
          <el-icon><Delete /></el-icon>
          <span>删除行</span>
        </el-button>
        <el-button size="small" type="danger" plain :disabled="readonly || columnCount === 0" @click="removeColumn">
          <el-icon><Delete /></el-icon>
          <span>删除列</span>
        </el-button>
        <el-button size="small" :disabled="readonly || rows.length === 0" @click="clearAll">
          <el-icon><DeleteFilled /></el-icon>
          <span>清空</span>
        </el-button>
      </div>
      <div class="toolbar-group">
        <el-dropdown :disabled="readonly || rows.length === 0" @command="sortByColumn">
          <el-button size="small" :disabled="readonly || rows.length === 0">
            <el-icon><Sort /></el-icon>
            <span>排序</span>
            <el-icon><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="asc">按选中列升序</el-dropdown-item>
              <el-dropdown-item command="desc">按选中列降序</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <el-button size="small" :disabled="readonly" @click="findDialogVisible = true">
          <el-icon><Search /></el-icon>
          <span>查找替换</span>
        </el-button>
        <el-button size="small" :disabled="readonly" @click="triggerImport">
          <el-icon><Upload /></el-icon>
          <span>导入</span>
        </el-button>
        <el-button size="small" type="primary" plain @click="exportXlsx">
          <el-icon><Download /></el-icon>
          <span>导出 Excel</span>
        </el-button>
        <el-button size="small" @click="exportCsv">
          <el-icon><Document /></el-icon>
          <span>导出 CSV</span>
        </el-button>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept=".xlsx,.xls,.csv"
        class="hidden-input"
        @change="onImportFile"
      />
    </div>

    <el-dialog
      v-model="findDialogVisible"
      title="查找替换"
      class="excel-find-dialog"
      width="420px"
    >
      <el-form label-position="top">
        <el-form-item label="查找内容">
          <el-input v-model="findText" placeholder="输入要查找的内容" clearable />
        </el-form-item>
        <el-form-item label="替换为">
          <el-input v-model="replaceText" placeholder="输入替换后的内容（留空表示删除）" clearable />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="findDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="doReplaceAll">全部替换</el-button>
      </template>
    </el-dialog>

    <div class="excel-table-wrap">
      <table v-if="rows.length" class="excel-table" :class="{ 'is-readonly': readonly }">
        <thead>
          <tr>
            <th class="corner" @click="selectedRow = null; selectedCol = null; selectedCell = null"></th>
            <th
              v-for="c in columnCount"
              :key="'col-' + c"
              class="col-head"
              :class="{ selected: selectedCol === c - 1 }"
              @click="selectCol(c - 1)"
            >{{ colLetter(c - 1) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, r) in rows" :key="'row-' + r">
            <td
              class="row-head"
              :class="{ selected: selectedRow === r }"
              @click="selectRow(r)"
            >{{ r + 1 }}</td>
            <td
              v-for="(cell, c) in row"
              :key="'cell-' + r + '-' + c"
              class="cell"
              :class="{ selected: isSelected(r, c), readonly }"
              :data-r="r"
              :data-c="c"
              :contenteditable="!readonly"
              @focus="selectCell(r, c)"
              @blur="updateCell(r, c, $event)"
              @keydown.enter.prevent="moveDown(r, c)"
              @paste.prevent="onPasteCell(r, c, $event)"
            >{{ cell }}</td>
          </tr>
        </tbody>
      </table>
      <el-empty v-else description="暂无数据，点击「添加行」或「导入」开始" :image-size="70" />
    </div>

    <div class="excel-status">
      <span>{{ rows.length }} 行 × {{ columnCount }} 列</span>
      <span v-if="selectedCell" class="status-cell">
        当前单元格：{{ colLetter(selectedCell.c) }}{{ selectedCell.r + 1 }}
      </span>
      <span v-if="readonly" class="status-readonly">只读模式</span>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ExcelEditor',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    fileName: {
      type: String,
      default: '表格',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rows: [],
      sheetName: 'Sheet1',
      selectedRow: null,
      selectedCol: null,
      selectedCell: null,
      history: [],
      historyIndex: -1,
      findDialogVisible: false,
      findText: '',
      replaceText: '',
    };
  },
  computed: {
    columnCount() {
      return this.rows.length ? Math.max(...this.rows.map(row => row.length)) : 0;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        this.rows = this.parseContent(val);
        this.history = [JSON.stringify(this.rows)];
        this.historyIndex = 0;
      },
    },
  },
  methods: {
    parseContent(str) {
      if (!str) return [];
      try {
        const data = JSON.parse(str);
        if (Array.isArray(data)) {
          return this.normalizeRows(data);
        }
        if (data && Array.isArray(data.rows)) {
          this.sheetName = data.name || 'Sheet1';
          return this.normalizeRows(data.rows);
        }
      } catch (e) {
        // 兼容旧的纯 HTML 表格内容
        const tableRows = this.htmlTableToRows(str);
        if (tableRows.length) {
          return tableRows;
        }
      }
      return [];
    },
    normalizeRows(input) {
      const maxCols = Math.max(0, ...input.map(row => Array.isArray(row) ? row.length : 0));
      return input.map(row => {
        if (!Array.isArray(row)) return [row == null ? '' : String(row)];
        const copy = row.slice(0, 100);
        while (copy.length < maxCols) copy.push('');
        return copy;
      }).slice(0, 1000);
    },
    htmlTableToRows(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      const trs = div.querySelectorAll('tr');
      const rows = [];
      trs.forEach(tr => {
        const cells = [];
        tr.querySelectorAll('th,td').forEach(td => {
          cells.push(td.innerText.trim());
        });
        if (cells.length) rows.push(cells);
      });
      return rows;
    },
    getContent() {
      return JSON.stringify({
        name: this.sheetName || 'Sheet1',
        rows: this.rows,
      });
    },
    snapshot() {
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(JSON.stringify(this.rows));
      if (this.history.length > 50) {
        this.history.shift();
      }
      this.historyIndex = this.history.length - 1;
    },
    undo() {
      if (this.historyIndex <= 0) {
        this.$message.info('没有可撤销的操作');
        return;
      }
      this.historyIndex--;
      this.rows = JSON.parse(this.history[this.historyIndex]);
      this.selectedRow = null;
      this.selectedCol = null;
      this.selectedCell = null;
    },
    redo() {
      if (this.historyIndex >= this.history.length - 1) {
        this.$message.info('没有可重做的操作');
        return;
      }
      this.historyIndex++;
      this.rows = JSON.parse(this.history[this.historyIndex]);
      this.selectedRow = null;
      this.selectedCol = null;
      this.selectedCell = null;
    },
    colLetter(index) {
      let n = index + 1;
      let s = '';
      while (n > 0) {
        const rem = (n - 1) % 26;
        s = String.fromCharCode(65 + rem) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    },
    isSelected(r, c) {
      return this.selectedCell && this.selectedCell.r === r && this.selectedCell.c === c;
    },
    selectCell(r, c) {
      if (!this.selectedCell || this.selectedCell.r !== r || this.selectedCell.c !== c) {
        this.snapshot();
      }
      this.selectedCell = { r, c };
      this.selectedRow = null;
      this.selectedCol = null;
    },
    selectRow(r) {
      this.selectedRow = r;
      this.selectedCol = null;
      this.selectedCell = null;
    },
    selectCol(c) {
      this.selectedCol = c;
      this.selectedRow = null;
      this.selectedCell = null;
    },
    updateCell(r, c, event) {
      const value = event.target.innerText;
      if (!this.rows[r]) this.rows[r] = [];
      this.rows[r][c] = value;
    },
    onPasteCell(r, c, event) {
      const text = event.clipboardData.getData('text');
      const lines = text.split(/\r?\n/).filter(line => line !== '');
      if (lines.length <= 1) {
        event.target.innerText = text;
        this.updateCell(r, c, { target: event.target });
        return;
      }
      // 多行粘贴时逐行填入
      lines.forEach((line, i) => {
        const targetRow = r + i;
        if (!this.rows[targetRow]) this.rows[targetRow] = [];
        this.rows[targetRow][c] = line;
      });
      event.target.innerText = lines[0];
      this.$forceUpdate();
    },
    addRow() {
      const cols = this.columnCount || 1;
      this.snapshot();
      this.rows.push(new Array(cols).fill(''));
      this.selectedRow = this.rows.length - 1;
    },
    addColumn() {
      const cols = this.columnCount;
      if (cols >= 100) {
        this.$message.warning('最多支持 100 列');
        return;
      }
      this.snapshot();
      if (!this.rows.length) {
        this.rows.push(['']);
        return;
      }
      this.rows.forEach(row => row.push(''));
      this.selectedCol = cols;
    },
    removeRow() {
      if (!this.rows.length) return;
      this.snapshot();
      const idx = this.selectedRow != null ? this.selectedRow : this.rows.length - 1;
      this.rows.splice(idx, 1);
      this.selectedRow = null;
    },
    removeColumn() {
      if (!this.rows.length) return;
      this.snapshot();
      const idx = this.selectedCol != null ? this.selectedCol : this.columnCount - 1;
      this.rows.forEach(row => {
        if (idx < row.length) row.splice(idx, 1);
      });
      this.selectedCol = null;
    },
    duplicateRow() {
      if (!this.rows.length) return;
      const idx = this.selectedRow != null ? this.selectedRow : this.rows.length - 1;
      this.snapshot();
      this.rows.splice(idx + 1, 0, this.rows[idx].slice());
      this.selectedRow = idx + 1;
    },
    duplicateColumn() {
      if (!this.rows.length) return;
      const idx = this.selectedCol != null ? this.selectedCol : this.columnCount - 1;
      this.snapshot();
      this.rows.forEach(row => {
        row.splice(idx + 1, 0, row[idx] == null ? '' : row[idx]);
      });
      this.selectedCol = idx + 1;
    },
    sortByColumn(direction) {
      if (this.selectedCol == null) {
        this.$message.warning('请先点击列号选择要排序的列');
        return;
      }
      const idx = this.selectedCol;
      this.snapshot();
      const header = this.rows[0] ? this.rows[0].slice() : [];
      const dataRows = this.rows.slice(1);
      dataRows.sort((a, b) => {
        const av = a[idx] == null ? '' : String(a[idx]);
        const bv = b[idx] == null ? '' : String(b[idx]);
        const an = Number(av);
        const bn = Number(bv);
        if (av !== '' && bv !== '' && !isNaN(an) && !isNaN(bn)) {
          return direction === 'asc' ? an - bn : bn - an;
        }
        return direction === 'asc'
          ? av.localeCompare(bv, 'zh-CN')
          : bv.localeCompare(av, 'zh-CN');
      });
      this.rows = [header, ...dataRows].filter(row => row.length);
      this.$message.success('排序完成');
    },
    doReplaceAll() {
      if (!this.findText) {
        this.$message.warning('请输入查找内容');
        return;
      }
      this.snapshot();
      let count = 0;
      this.rows = this.rows.map(row => row.map(cell => {
        if (cell == null) return cell;
        const s = String(cell);
        if (!s.includes(this.findText)) return cell;
        count += s.split(this.findText).length - 1;
        return s.split(this.findText).join(this.replaceText);
      }));
      this.findDialogVisible = false;
      this.findText = '';
      this.replaceText = '';
      this.$message.success(`已替换 ${count} 处`);
    },
    clearAll() {
      this.$confirm('确定要清空表格所有内容吗？', '提示', {
        confirmButtonText: '清空',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.snapshot();
        this.rows = [];
        this.selectedRow = null;
        this.selectedCol = null;
        this.selectedCell = null;
        this.$message.success('已清空');
      }).catch(() => {});
    },
    triggerImport() {
      this.$refs.fileInput.click();
    },
    async onImportFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (data.length > 1000) {
          this.$message.warning('表格行数超过 1000，已截断导入');
        }
        if (data[0] && data[0].length > 100) {
          this.$message.warning('表格列数超过 100，已截断导入');
        }
        this.snapshot();
        this.rows = this.normalizeRows(data);
        this.sheetName = wb.SheetNames[0] || 'Sheet1';
        this.selectedRow = null;
        this.selectedCol = null;
        this.selectedCell = null;
        this.$message.success(`导入成功，共 ${this.rows.length} 行`);
      } catch (err) {
        console.error(err);
        this.$message.error('文件解析失败，请确认是有效的 Excel/CSV 文件');
      } finally {
        event.target.value = '';
      }
    },
    exportXlsx() {
      if (!this.rows.length) {
        this.$message.warning('表格暂无数据');
        return;
      }
      const ws = XLSX.utils.aoa_to_sheet(this.rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, (this.sheetName || 'Sheet1').slice(0, 31));
      XLSX.writeFile(wb, `${this.fileName || '表格'}.xlsx`);
    },
    exportCsv() {
      if (!this.rows.length) {
        this.$message.warning('表格暂无数据');
        return;
      }
      const csv = this.rows.map(row =>
        row.map(cell => {
          const s = cell == null ? '' : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(',')
      ).join('\r\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.fileName || '表格'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    moveDown(r, c) {
      if (!this.rows[r + 1]) {
        this.addRow();
      }
      this.$nextTick(() => {
        const el = this.$el.querySelector(`td[data-r="${r + 1}"][data-c="${c}"]`);
        if (el) {
          el.focus();
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(el);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      });
    },
  },
};
</script>

<style scoped>
.excel-editor {
  width: 100%;
  --el-color-primary: #4f6ef7;
  --el-color-primary-light-3: #7b93fa;
  --el-color-primary-light-5: #a5b6fb;
  --el-color-primary-light-7: #cdd8fd;
  --el-color-primary-light-8: #e2e9fe;
  --el-color-primary-light-9: #f0f4ff;
  --el-color-primary-dark-2: #3f58c6;
}

.excel-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: #f8faff;
  border: 1px solid #e7ecf7;
  border-radius: 10px;
  margin-bottom: 12px;
}

.toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.toolbar-group .el-button {
  margin: 0;
}

.sheet-name-input {
  width: 150px;
}

.hidden-input {
  display: none;
}

.excel-table-wrap {
  overflow: auto;
  max-height: 62vh;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
}

.excel-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 100%;
  font-size: 13px;
}

.excel-table th,
.excel-table td {
  border-right: 1px solid #eef0f4;
  border-bottom: 1px solid #eef0f4;
  padding: 7px 10px;
  min-width: 90px;
  max-width: 260px;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.excel-table .corner,
.excel-table .row-head,
.excel-table .col-head {
  position: sticky;
  background: #f8fafc;
  color: #6b7280;
  font-weight: 600;
  text-align: center;
  user-select: none;
  z-index: 1;
}

.excel-table .corner {
  left: 0;
  top: 0;
  z-index: 3;
  cursor: default;
}

.excel-table .row-head {
  left: 0;
  z-index: 2;
  min-width: 44px;
  width: 44px;
  cursor: pointer;
}

.excel-table .col-head {
  top: 0;
  z-index: 2;
  min-width: 90px;
  cursor: pointer;
}

.excel-table .row-head.selected,
.excel-table .col-head.selected {
  background: var(--el-color-primary-light-8);
  color: var(--el-color-primary);
}

.excel-table .cell {
  background: #ffffff;
  outline: none;
}

.excel-table .cell.selected {
  background: var(--el-color-primary-light-9);
  box-shadow: inset 0 0 0 2px var(--el-color-primary);
}

.excel-table .cell.readonly {
  cursor: default;
}

.excel-table .cell:not(.readonly):hover {
  background: #f7f9ff;
}

.excel-table .cell:not(.readonly):focus {
  background: #ffffff;
  box-shadow: inset 0 0 0 2px var(--el-color-primary);
}

.excel-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.status-cell {
  color: var(--el-color-primary);
  font-weight: 500;
}

.status-readonly {
  color: #f59e0b;
}
</style>

<style>
.excel-find-dialog {
  --el-color-primary: #4f6ef7;
  --el-color-primary-light-3: #7b93fa;
  --el-color-primary-light-5: #a5b6fb;
  --el-color-primary-light-7: #cdd8fd;
  --el-color-primary-light-8: #e2e9fe;
  --el-color-primary-light-9: #f0f4ff;
  --el-color-primary-dark-2: #3f58c6;
}

.excel-find-dialog .el-dialog__title {
  font-weight: 600;
}
</style>
